<script setup lang="ts">
import { ref, nextTick, watch, onMounted } from 'vue'
import { motion } from 'motion-v'
import { useChatbotStore } from '../stores/chatbotStore'
import AiChatbotMessage from './AiChatbotMessage.vue'
import AiChatbotTypingIndicator from './AiChatbotTypingIndicator.vue'

const emit = defineEmits<{
  close: []
}>()

const store = useChatbotStore()
const inputText = ref('')
const messagesEl = ref<HTMLDivElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)

function handleSend() {
  const text = inputText.value.trim()
  if (!text) return
  store.sendMessage(text)
  inputText.value = ''
  nextTick(() => inputRef.value?.focus())
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    handleSend()
  }
}

function scrollToBottom() {
  nextTick(() => {
    const el = messagesEl.value
    if (el) el.scrollTop = el.scrollHeight
  })
}

// Reserved space below the messages. While a reply streams in we reserve a full
// viewport of height so its top can sit right under the header; the reply then
// grows downward into that space and its start stays put — a long answer is
// never scrolled to its end (DEV-980).
const spacerHeight = ref(0)

// Pull the streaming reply's top just under the header, once. The reserved
// spacer guarantees the scroll position is reachable immediately; as the
// typewriter fills the message downward the top stays anchored.
function anchorReplyUnderHeader(id: string) {
  nextTick(() => {
    const el = messagesEl.value
    if (!el) return
    spacerHeight.value = el.clientHeight
    nextTick(() => {
      const node = el.querySelector<HTMLElement>(`[data-mid="${id}"]`)
      if (node) el.scrollTop = node.offsetTop
    })
  })
}

watch(
  () => store.messages.length,
  () => {
    const last = store.messages[store.messages.length - 1]
    if (!last) return

    if (last.role === 'user') {
      // Show the sent question and the "печатает" indicator below it.
      scrollToBottom()
    } else if (last.streaming) {
      // Answer arrived — pull it under the header and let it type downward.
      anchorReplyUnderHeader(last.id)
    } else {
      // Greeting or restored history — show the latest.
      scrollToBottom()
    }
  },
)

// Release the reserved space once the reply is complete.
watch(
  () => store.messages.some((m) => m.streaming),
  (streaming) => {
    if (!streaming) spacerHeight.value = 0
  },
)

onMounted(() => {
  scrollToBottom()
  nextTick(() => inputRef.value?.focus())
})
</script>

<template>
  <div class="chat-window">
    <header class="chat-window__header">
      <div class="chat-window__header-info">
        <div class="chat-window__logo">AI</div>
        <div class="chat-window__title">
          <span class="chat-window__name">VINPROP AI</span>
          <span
            class="chat-window__status"
            :class="{ 'chat-window__status--online': store.isConnected }"
          >
            <span class="chat-window__status-dot" />
            {{ store.isConnected ? 'онлайн' : 'подключение…' }}
          </span>
        </div>
      </div>
      <button class="chat-window__close" aria-label="Закрыть чат" @click="emit('close')">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M5 5L15 15M15 5L5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>
    </header>

    <div ref="messagesEl" class="chat-window__messages">
      <motion.div
        v-for="msg in store.messages"
        :key="msg.id"
        :data-mid="msg.id"
        class="msg-row"
        :initial="{ opacity: 0, y: 8 }"
        :animate="{ opacity: 1, y: 0 }"
        :transition="{ type: 'spring', stiffness: 520, damping: 36 }"
      >
        <AiChatbotMessage :message="msg" />
      </motion.div>

      <div v-if="store.isTyping && !store.messages.some((m) => m.streaming)" class="msg-row">
        <AiChatbotTypingIndicator />
      </div>

      <div v-if="spacerHeight" class="chat-window__spacer" :style="{ height: spacerHeight + 'px' }" />
    </div>

    <div v-if="store.error" class="chat-window__error">
      {{ store.error }}
    </div>

    <footer class="chat-window__footer">
      <input
        ref="inputRef"
        v-model="inputText"
        type="text"
        class="chat-window__input"
        placeholder="Введите сообщение…"
        :disabled="!store.isConnected"
        @keydown="handleKeydown"
      />
      <motion.button
        class="chat-window__send"
        :disabled="!inputText.trim() || !store.isConnected || store.isTyping"
        :hover="{ scale: 1.06 }"
        :press="{ scale: 0.92 }"
        :transition="{ type: 'spring', stiffness: 400, damping: 18 }"
        aria-label="Отправить"
        @click="handleSend"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M3 10L17 3L10 17L9 11L3 10Z" fill="currentColor" />
        </svg>
      </motion.button>
    </footer>
  </div>
</template>

<style scoped>
.chat-window {
  display: flex;
  flex-direction: column;
  width: 380px;
  height: 540px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.chat-window__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: #2563eb;
  color: white;
  flex-shrink: 0;
}

.chat-window__header-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.chat-window__logo {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}

.chat-window__title {
  display: flex;
  flex-direction: column;
}

.chat-window__name {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.chat-window__status {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  opacity: 0.75;
}

.chat-window__status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #cbd5e1;
}

.chat-window__status--online {
  opacity: 1;
}

.chat-window__status--online .chat-window__status-dot {
  background: #4ade80;
}

.chat-window__close {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.chat-window__close:hover {
  background: rgba(255, 255, 255, 0.15);
}

.chat-window__messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  background: #f9fafb;
}

.msg-row {
  display: flex;
  flex-direction: column;
}

.chat-window__spacer {
  flex-shrink: 0;
}

.chat-window__messages::-webkit-scrollbar {
  width: 4px;
}

.chat-window__messages::-webkit-scrollbar-track {
  background: transparent;
}

.chat-window__messages::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 2px;
}

.chat-window__error {
  padding: 8px 16px;
  background: #fef2f2;
  color: #dc2626;
  font-size: 12px;
  text-align: center;
  flex-shrink: 0;
}

.chat-window__footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  background: white;
  flex-shrink: 0;
}

.chat-window__input {
  flex: 1;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  padding: 10px 16px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s;
  font-family: inherit;
  color: #1f2937;
}

.chat-window__input:focus {
  border-color: #2563eb;
}

.chat-window__input:disabled {
  background: #f9fafb;
  color: #9ca3af;
}

.chat-window__input::placeholder {
  color: #9ca3af;
}

.chat-window__send {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: #2563eb;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.chat-window__send:disabled {
  background: #d1d5db;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .chat-window {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    border-radius: 0;
    position: fixed;
    inset: 0;
  }
}
</style>
